import { TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, output, signal } from '@angular/core';
import {
  email,
  FieldTree,
  form,
  FormField,
  FormOptions,
  FormRoot,
  required,
  SchemaPathTree,
  TreeValidationResult,
} from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';

import { AlertComponent } from '../../components/alert/alert.component';
import { FeatherIconsComponent } from '../../components/feather-icons/feather-icons.component';
import { ValidationMessageDirective } from '../../directives/validation-message/validation-message.directive';
import { OAuthProvider } from '../../model/oauth-provider.enums';
import { WINDOW } from '../../providers/window/window.provider';
import { OauthService } from '../../services/oauth/oauth.service';
import { LoginPasskeyService } from '../../services/passkey/login-passkey.service';
import { PasskeyService } from '../../services/passkey/passkey.service';
import { SignalFormService } from '../../services/signal-form/signal-form.service';
import { WebConfigService } from '../../services/web-config/web-config.service';
import { FormFieldOnErrorDirective } from '../../directives/form-field-on-error/form-field-on-error.directive';

type OAuthProviderObject = { enabled: boolean; provider: OAuthProvider; icon?: string };

@Component({
  selector: 'app-oauth-login',
  imports: [
    TranslatePipe,
    TitleCasePipe,
    AlertComponent,
    FeatherIconsComponent,
    FormFieldOnErrorDirective,
    FormField,
    FormRoot,
  ],
  templateUrl: './oauth-login.component.html',
  styleUrls: ['./oauth-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OauthLoginComponent implements OnInit {
  #webConfig = inject(WebConfigService);
  #oauthService = inject(OauthService);
  #passkeyService = inject(PasskeyService);
  #loginPasskeyService = inject(LoginPasskeyService);
  #logger = inject(NGXLogger);
  #window = inject(WINDOW);
  loggedIn = output<string>();

  isAllowedToCreateAccount: boolean;
  portalId: string;
  providers: OAuthProviderObject[] = [
    { enabled: true, provider: OAuthProvider.Google, icon: 'google' },
    { enabled: true, provider: OAuthProvider.GitHub, icon: 'github' },
    { enabled: false, provider: OAuthProvider.Microsoft, icon: 'microsoft' },
  ];

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  formModel = signal<{ email: string }>({ email: '' });
  formOptions: FormOptions<{ email: string }> = SignalFormService.formSubmitOptions(
    this.loginWithOTP.bind(this),
    this.#onInvalid.bind(this),
  );
  form = form(this.formModel, this.#formValidation.bind(this), this.formOptions);

  login = (provider: OAuthProvider): void => this.#oauthService.login(provider);

  ngOnInit(): void {
    this.portalId = this.#webConfig.portalId;
    this.isAllowedToCreateAccount = this.#webConfig.isAllowedToCreateAccount;
    this.form().valid();
  }

  async loginWithOTP(): Promise<TreeValidationResult> {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);
    this.loggedIn.emit(this.form.email().value());
    return undefined;
  }

  loginWithPasskey(): void {
    if (!this.#window.navigator.credentials?.get) {
      this.errorMessage.set('Passkeys are not supported on this browser or device.');
      return;
    }
    const email = this.#validateForm();
    if (!email) {
      return;
    }

    this.#passkeyService.getLoginOptions(email).subscribe({
      next: (options) => this.#loginPasskeyService.handlePasskeyLogin(email, options),
      error: (err) => {
        this.#logger.error('Failed to get passkey options', err);
        this.errorMessage.set(
          err.error?.message || 'Passkey authentication failed. Please try again or use Magic Link.',
        );
        this.isLoading.set(false);
      },
    });
  }

  registerPasskey(): void {
    if (!this.#window.navigator.credentials?.create) {
      this.errorMessage.set('Passkey registration is not supported on this device/browser.');
      return;
    }
    const email = this.#validateForm();
    if (!email) {
      return;
    }

    this.#passkeyService.getRegisterOptions(email).subscribe({
      next: (options) => this.#loginPasskeyService.handlePasskeyCreation(email, options),
      error: (err) => {
        this.#logger.error('Failed to get passkey creation options', err);
        this.errorMessage.set(err.error?.message || 'Failed to initialize passkey registration.');
        this.isLoading.set(false);
      },
    });
  }

  loginWithMagicLink(): void {
    const email = this.#validateForm();
    if (!email) {
      return;
    }

    this.#passkeyService.sendMagicLink(email).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.successMessage.set('LABELS.MAGIC_LINK_SENT');
      },
      error: (err) => {
        this.#logger.error('Magic link request failed', err);
        this.errorMessage.set(err.error?.message || 'Failed to send Magic Link.');
        this.isLoading.set(false);
      },
    });
  }

  #onInvalid(): void {
    this.form().markAsTouched();
  }

  #formValidation(path: SchemaPathTree<{ email: string }>): void {
    required(path.email);
    email(path.email);
  }

  #validateForm(): string {
    if (!this.form().valid()) {
      this.form().markAsTouched();
      return '';
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    const email = this.form.email().value();
    return email;
  }
}
